import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'card',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

const FaqRenderer = ({ block, theme = {} }) => {
  const { content } = block;
  const { heading, items = [] } = content;

  const [expandedItems, setExpandedItems] = useState(new Set());
  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  const toggleItem = (itemId) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const validItems = items.filter((item) => item.question?.trim());

  if (validItems.length === 0) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-3xl mx-auto text-center" style={{ color: styles.mutedColor }}>
          <p>No FAQ items added yet</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-3xl mx-auto">
        {heading && (
          <h3
            className={`${styles.headingSizeClass} font-bold ${styles.headingAlignmentClass} mb-6 sm:mb-8 md:mb-10`}
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            {heading}
          </h3>
        )}

        <div className="space-y-3 sm:space-y-4">
          {validItems.map((item) => {
            const isExpanded = expandedItems.has(item.id);

            return (
              <div
                key={item.id}
                className="rounded-lg overflow-hidden"
                style={{
                  backgroundColor: styles.cardBackground,
                  border: `1px solid ${styles.borderColor}`,
                }}
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between text-left transition-colors min-h-[44px]"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = styles.isDark ? '#252540' : '#f0f0f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span
                    className="text-sm sm:text-base font-medium pr-4"
                    style={{ color: styles.headingColor }}
                  >
                    {item.question}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`flex-shrink-0 transition-transform duration-200 ${
                      isExpanded ? 'transform rotate-180' : ''
                    }`}
                    style={{ color: styles.mutedColor }}
                  />
                </button>

                <div
                  className={`overflow-hidden transition-all duration-200 ${
                    isExpanded ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0">
                    <p
                      className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap opacity-90"
                      style={{ color: styles.bodyColor, fontFamily: styles.bodyFontStyle }}
                    >
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FaqRenderer;
